import React, { useState } from 'react';
import {
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import DeleteExtraction from './DeleteExtraction';
import DownloadDocument from '../../common/DownloadDocument';
import DownloadExtraction from './DownloadExtraction';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { IExtraction, IExtractionBatch } from '../../../types/my-types';
import { Link } from 'react-router';
import { format } from 'date-fns';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';
import { useQueryClient } from '@tanstack/react-query';

export interface DocumentListGroupProps {
  extractionBatch: IExtractionBatch;
  extractionIds: string[];
  groupId: string;
  isClassifier?: boolean;
}
// TODO: allow unclassfied group to download
const DocumentListGroup: React.FC<DocumentListGroupProps> = ({
  extractionBatch,
  extractionIds,
  groupId,
  isClassifier,
}) => {
  const { dataPoolId, isStaff } = React.useContext(AppContext);

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const [extractions, setExtractions] = React.useState<Array<IExtraction>>([]);
  const client = useQueryClient();
  const glynt = useGlynt();
  React.useEffect(() => {
    setLoading(true);
    if (extractionIds.length > 0) {
      Promise.all(
        extractionIds
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map(async (extractionId: string) => {
            return client.fetchQuery({
              queryKey: ['extraction', extractionId],
              queryFn: async () => await (await glynt).extraction.retrieve(extractionId),
              staleTime: 10000
            })
              .then(async (extraction) => {
                let docId = getIdFromUrl(extraction.document);
                let doc = await client.fetchQuery({
                  queryKey: ['document', docId],
                  queryFn: async () => await (await glynt).document.retrieve(docId),
                  staleTime: 10000
                });
                let extractionWithDoc = Object.assign({}, extraction);
                extractionWithDoc.doc = doc;
                return extractionWithDoc;
              });
          })
      )
        .then((result) => {
          setExtractions(result);
          setLoading(false);
        })
        .catch(console.error);
    } else {
      setExtractions([]);
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [extractionIds, page, rowsPerPage]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Paper id="documents-list" style={{ width: '100%', overflowX: 'auto' }}>
      <Table style={{ minWidth: 700 }}>
        <TableHead style={{ backgroundColor: '#E5E3E3', fontWeight: 'bold' }}>
          <TableRow>
            <TableCell>Document</TableCell>
            {isClassifier ? <TableCell>Glynt Tag</TableCell> : null}
            <TableCell align="center">Uploaded at</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Finished at</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {extractions.map((extraction: IExtraction) => {
            if (extraction.doc) {
              return (
                <TableRow key={extraction.id}>
                  <TableCell component="th" scope="row">
                    {extraction.doc.label}
                  </TableCell>
                  {isClassifier ? <TableCell>{extraction.glynt_tags.join(',')}</TableCell> : null}
                  <TableCell align="center">
                    {format(new Date(extraction.doc.created_at), 'yyyy-MM-dd HH:mm:ss')}
                  </TableCell>
                  <TableCell align="center">
                    {extraction ? extraction.status : 'Auto-Classify'}
                  </TableCell>
                  <TableCell align="center">
                    {extraction && extraction.finished
                      ? format(new Date(extraction.updated_at), 'yyyy-MM-dd HH:mm:ss')
                      : 'In Progress'}
                  </TableCell>
                  <TableCell align="center">
                    {extraction && extraction.finished ? (
                      <>
                        <DownloadExtraction
                          failed={extraction.status === 'Failed'}
                          extractionId={extraction.id}
                          docLabel={extraction.doc.label}
                        />
                        <Link
                          to={`/data-pools/${dataPoolId}/extraction-batch/${extractionBatch.id}/review/${extraction.id}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Button color="inherit">Review</Button>
                        </Link>

                        <DeleteExtraction
                          extractionId={extraction.id}
                          extractionBatchLabel={extraction.doc.label}
                        />
                        {isStaff ? (
                          <DownloadDocument
                            documentId={extraction.doc.id}
                            documentContentType={extraction.doc.content_type}
                            documentLabel={extraction.doc.label}
                            documentFileExtension={extraction.doc.file_extension}
                            iconType="cloud"
                          />
                        ) : null}
                      </>
                    ) : (
                      <>In Progress </>
                    )}
                  </TableCell>
                </TableRow>
              );
            } else {
              return null;
            }
          })}
        </TableBody>
        {extractionIds.length > 0 && <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={5}
              count={extractionIds.length}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                'aria-label': 'Previous Page',
              }}
              nextIconButtonProps={{
                'aria-label': 'Next Page',
              }}
              onPageChange={(e, p) => {
                e?.preventDefault();
                if (p > page) {
                  setPage(p);
                } else {
                  setPage(p);
                }
              }}
              onRowsPerPageChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                setPage(0);
                setRowsPerPage(parseInt(e.target.value, 10));
              }}
            />
          </TableRow>
        </TableFooter>}
      </Table>
    </Paper>
  );
};

export default DocumentListGroup;
